import { Injectable } from '@angular/core';
import { SubscribeManager, SubscribeManagerUnsubscribe } from '@app/data/class/subscribe-manager.class';
import moment from 'moment';
import { APIWithLoaderHelper } from '@app/data/helpers/apiWithLoaderHelper';

export type FilterSetupType = 'taiao' | 'sitehealth' | 'maramataka' | 'mapview';
export type FilterSetupDomain = 'overview' | 'hau' | 'wai' | 'moana' | 'whenua';
export type FilterSetupSensor = 'air' | 'waterway' | 'groundwater' | 'coastal';
export type FilterDrawMapFeatures = {
  [domain: string]: {
    id: string;
    display: string;
  }[];
};
export type FilterSetupBaseMap = 'orthophoto' | 'topographical';
export type FilterSetupDataLayer = 'odour' | 'particulate' | 'pm10' | 'pm2-5';
export type FilterSetupMatauranga = 'hau' | 'wai' | 'moana' | 'whenua';
export type FilterSetupMonitoring = 'hau' | 'wai' | 'moana' | 'whenua' | 'waste';
export type FilterSetupObservation = 'text' | 'multimedia';
export type FilterSetupCalendarType = 'day' | 'week' | 'month';

export interface FilterSetup {
  [key: string]: any;
  domain: FilterSetupDomain;
  fromDate: Date;
  toDate: Date;
  searchTerm: string;
  sensor: FilterSetupSensor;
  mapFeatures: string[];
  mapGroups: string[];
  mapFeaturesOrder: string[];
  baseMap: FilterSetupBaseMap;
  dataLayer: FilterSetupDataLayer;
  matauranga: FilterSetupMatauranga[];
  monitoring: FilterSetupMonitoring[];
  observation: FilterSetupObservation[];
  calendarType: FilterSetupCalendarType;
  calendarTypeYear: number;
  calendarTypeMonth: number;
  calendarTypeWeek: number;
  calendarTypeDay: number;
  selectedLocation: string;
  selectedEnvironmentalDataSources: string[];
  selectedEnvironmentalDataSourceDashGroups: string[];
  selectedContributingOrganisations: string[];
  selectedContributingProjectListItems: string[];
}

export interface FilterSetupSubscribe {
  filters: FilterSetup;
  previousFilters: FilterSetup;
  changedFilters?: FilterSetupKeys;
}

type FilterSetupKeys = (keyof FilterSetup)[];

export type FilterSubscribeCallback = (setup: FilterSetupSubscribe) => void;
export type FilterUnsubscribe = () => void;

@Injectable({
  providedIn: 'root',
})
export class FilterService extends SubscribeManager<FilterSetupSubscribe, [FilterSetupKeys?]> {
  private static readonly STORAGE_PREFIX = 'filter:';
  private static readonly STORAGE_KEYS = {
    mapFeatures: `${FilterService.STORAGE_PREFIX}mapFeatures`,
    mapGroups: `${FilterService.STORAGE_PREFIX}mapGroups`,
    mapFeaturesOrder: `${FilterService.STORAGE_PREFIX}mapFeaturesOrder`,
  } as const;

  public defaultMapFilters = [];
  private filters: FilterSetup = {
    domain: 'overview',
    fromDate: moment().subtract(3, 'month').toDate(),
    toDate: new Date(),
    searchTerm: '',
    selectedLocation: 'all',
    selectedEnvironmentalDataSources: ['all'],
    selectedEnvironmentalDataSourceDashGroups: ['all'],
    selectedContributingOrganisations: ['all'],
    selectedContributingProjectListItems: ['all'],
    sensor: 'groundwater',
    mapFeatures: ['monitor'],
    mapGroups: [],
    mapFeaturesOrder: [],
    baseMap: 'orthophoto',
    dataLayer: 'odour',
    matauranga: ['hau', 'moana', 'wai', 'whenua'],
    monitoring: ['hau', 'moana', 'wai', 'waste', 'whenua'],
    observation: ['multimedia', 'text'],
    calendarType: 'day',
    calendarTypeYear: moment().year(),
    calendarTypeMonth: moment().month(),
    calendarTypeWeek: 1,
    calendarTypeDay: moment().date(),
  };
  private previousFilters: FilterSetup = this.getFilters();
  private changedFilters: FilterSetupKeys = [];

  constructor(private apiWithLoaderHelper: APIWithLoaderHelper) {
    super();
    this.restorePersistedFilters();
    this.previousFilters = this.getFilters();
  }

  /**
   * Subscribe to filter changes.
   *
   * If no optionalFilters are provided, the callback is invoked on every change.
   * If optionalFilters are provided, the callback is only invoked when at least one of the specified filters changes.
   * If the publisher doesn't provide changedFilters, the callback is always invoked.
   *
   * @param callback Callback function to invoke on filter changes.
   * @param optionalFilters Optional array of filter keys to listen for changes.
   * @returns Unsubscribe function to stop listening for changes.
   */
  public subscribe(callback: FilterSubscribeCallback, optionalFilters?: FilterSetupKeys): SubscribeManagerUnsubscribe {
    return super.subscribe((setup: FilterSetupSubscribe) => {
      if (!Array.isArray(optionalFilters)) {
        callback(setup);
        return;
      }

      if (!Array.isArray(setup.changedFilters) || setup.changedFilters.length === 0) {
        callback(setup);
        return;
      }

      if (optionalFilters.some((x) => setup.changedFilters!.includes(x))) {
        callback(setup);
      }
    });
  }

  /**
   * Gets the current filter settings.
   * Gets filters such as domain, date range, search term, sensor type, map features, base map, data layer, matauranga,
   * monitoring, observation types, calendar type and selected location.
   *
   * @returns The current filter settings.
   */
  public getFilters(): FilterSetup {
    return {
      ...this.filters,
      mapFeatures: this.filters.mapFeatures.slice(),
      mapGroups: this.filters.mapGroups.slice(),
      mapFeaturesOrder: this.filters.mapFeaturesOrder.slice(),
      matauranga: this.filters.matauranga.slice(),
      monitoring: this.filters.monitoring.slice(),
      observation: this.filters.observation.slice(),
      selectedEnvironmentalDataSourceDashGroups: this.filters.selectedEnvironmentalDataSourceDashGroups.slice(),
      selectedEnvironmentalDataSources: this.filters.selectedEnvironmentalDataSources.slice(),
      selectedContributingOrganisations: this.filters.selectedContributingOrganisations.slice(),
      selectedContributingProjectListItems: this.filters.selectedContributingProjectListItems.slice(),
    };
  }

  /**
   * Sets the filter options.
   *
   * Cancels any active API requests that may be in-flight and are now stale.
   * @param filters The new filter options.
   * @returns void
   */
  public setFilters(filters: Partial<FilterSetup>): void {
    this.changedFilters = (Object.keys(filters) as FilterSetupKeys).reduce((accumulator, key) => {
      if (!Object.prototype.hasOwnProperty.call(this.filters, key)) {
        return accumulator;
      }

      const currentValue = this.filters[key];
      const incomingValue = filters[key];

      const changed =
        Array.isArray(incomingValue) || Array.isArray(currentValue)
          ? !this.areArraysShallowEqual(incomingValue as any[], currentValue as any[])
          : incomingValue !== currentValue;

      if (changed) {
        accumulator.push(key);
      }

      return accumulator;
    }, [] as FilterSetupKeys);

    if (this.changedFilters.length === 0) {
      return;
    }

    console.debug('FilterService.setFilters: filters changed, cancelling active API requests', this.changedFilters);
    this.apiWithLoaderHelper.cancelAll();

    this.filters = { ...this.filters, ...filters };

    this.applyMapFeatureOrderNormalization();
    this.persistFilterChanges(this.changedFilters);

    this.pushChange({
      filters: this.getFilters(),
      previousFilters: { ...this.previousFilters },
      changedFilters: [...this.changedFilters],
    });

    this.previousFilters = this.getFilters();
  }

  /**
   * Normalizes the order of map features based on their current selection.
   *
   * Perhaps the user has added or removed features, so ensure the order array reflects the current selection.
   * If new features are added, they are appended to the end of the order array.
   * If features are removed, they are removed from the order array.
   *
   * Performance: This method is only invoked if 'mapFeatures' or 'mapFeaturesOrder' have changed.
   * @returns void
   */
  private applyMapFeatureOrderNormalization(): void {
    if (!this.changedFilters.includes('mapFeatures') && !this.changedFilters.includes('mapFeaturesOrder')) {
      return;
    }

    const mapFeatures = Array.isArray(this.filters.mapFeatures) ? this.filters.mapFeatures.slice() : [];
    const existingOrder = Array.isArray(this.filters.mapFeaturesOrder) ? this.filters.mapFeaturesOrder.slice() : [];

    const layerKeys = mapFeatures.filter((key) => /^map-layer-\d+/.test(key));
    const normalizedOrder: string[] = [];
    const seen = new Set<string>();

    existingOrder.forEach((key) => {
      if (layerKeys.includes(key) && !seen.has(key)) {
        seen.add(key);
        normalizedOrder.push(key);
      }
    });

    layerKeys.forEach((key) => {
      if (!seen.has(key)) {
        seen.add(key);
        normalizedOrder.push(key);
      }
    });

    if (!this.areArraysShallowEqual(normalizedOrder, existingOrder)) {
      this.filters = { ...this.filters, mapFeaturesOrder: normalizedOrder };
      if (!this.changedFilters.includes('mapFeaturesOrder')) {
        this.changedFilters.push('mapFeaturesOrder');
      }
    }
  }

  /**
   * Persists filter changes to local storage.
   * @param keys The keys of the filters that have changed.
   * @returns void
   */
  private persistFilterChanges(keys: FilterSetupKeys): void {
    if (!this.hasLocalStorage()) {
      return;
    }

    keys.forEach((key) => {
      if (key === 'mapFeatures' || key === 'mapGroups' || key === 'mapFeaturesOrder') {
        const storageKey = (FilterService.STORAGE_KEYS as Record<string, string>)[key];
        const value = this.filters[key];

        try {
          if (Array.isArray(value) && value.length) {
            window.localStorage.setItem(storageKey, JSON.stringify(value));
          } else {
            window.localStorage.removeItem(storageKey);
          }
        } catch (e) {
          console.debug('FilterService.persistFilterChanges error', key, e);
        }
      }
    });
  }

  /**
   * Restores persisted filters from local storage.
   * @returns void
   */
  private restorePersistedFilters(): void {
    if (!this.hasLocalStorage()) {
      return;
    }

    const restored: Partial<FilterSetup> = {};

    (['mapFeatures', 'mapGroups', 'mapFeaturesOrder'] as FilterSetupKeys).forEach((key) => {
      const storageKey = (FilterService.STORAGE_KEYS as Record<string, string>)[key];
      try {
        const raw = window.localStorage.getItem(storageKey);
        if (!raw) {
          return;
        }
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          restored[key] = parsed.filter((item) => typeof item === 'string');
        }
      } catch (e) {
        console.debug('FilterService.restorePersistedFilters error', key, e);
      }
    });

    if (Object.keys(restored).length) {
      this.filters = { ...this.filters, ...restored };
    }
  }

  /**
   * Checks if the browser supports local storage.
   * @returns True if local storage is available, false otherwise.
   */
  private hasLocalStorage(): boolean {
    try {
      return typeof window !== 'undefined' && !!window.localStorage;
    } catch (_e) {
      return false;
    }
  }

  /**
   * Compares two arrays for shallow equality.
   * @param a The first array to compare.
   * @param b The second array to compare.
   * @returns True if the arrays are shallowly equal, false otherwise.
   */
  private areArraysShallowEqual(a?: any[], b?: any[]): boolean {
    const arrA = Array.isArray(a) ? a : [];
    const arrB = Array.isArray(b) ? b : [];

    if (arrA.length !== arrB.length) {
      return false;
    }

    for (let i = 0; i < arrA.length; i++) {
      if (arrA[i] !== arrB[i]) {
        return false;
      }
    }

    return true;
  }
}
